import { MCA_ENTITIES } from "./mcaEntities";

export const RENEWAL_WATCH_FIELDS = [
  "status",
  "code",
  "date",
  "client",
  "product",
  "provider",
  "productType",
  "consultants",
  "order",
  "debitNote",
  "period.start",
  "period.end",
  "renewalCount",
  "insurerNoticeDate",
  "confirmedAt",
  "rejection.date",
  "rejection.reason",
  "rejection.remark",
  "delivery.date",
  "delivery.remark",
  "receivedRenewal.date",
  "receivedRenewal.remark",
  "total",
  "costTotal",
  "currency",
  "insuredName",
  "lastPolicyNumber",
  "address",
  "description",
  "createdBy",
  "to",
  "discountAmount",
  "commissionRate",
  "internalRemark",
];

export const NOTIFICATION_ENTITIES = [
  { value: "debitNote", label: "Debit Note" },
  { value: "order", label: "Order" },
  { value: "client", label: "Client" },
  { value: "renewal", label: "Renewal" },
];

export const NOTIFICATION_EVENTS = [
  { value: "CREATE", label: "Create" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
];

export const NOTIFICATION_CHANNELS = [
  { value: "EMAIL", label: "Email" },
  { value: "IN_APP", label: "In-App" },
];

export const channelLabel = (value) =>
  NOTIFICATION_CHANNELS.find((c) => c.value === value)?.label ?? value;

export const entityLabel = (value) =>
  NOTIFICATION_ENTITIES.find((e) => e.value === value)?.label ??
  (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

export const eventLabel = (value) => {
  const normalized = value === "FIELD_CHANGE" ? "UPDATE" : value;
  return (
    NOTIFICATION_EVENTS.find((e) => e.value === normalized)?.label ?? normalized
  );
};

export const NOTIFICATION_PRIORITIES = [
  { value: "urgent", label: "Urgent" },
  { value: "important", label: "Important" },
  { value: "normal", label: "Normal" },
];

export const RECIPIENT_TYPES = [
  { value: "SPECIFIC_USERS", label: "Specific Users" },
  { value: "DOCUMENT_ROLE", label: "Document Role" },
  { value: "USER_ROLE", label: "User Role" },
];

export const DOCUMENT_ROLES = {
  debitNote: ["consultants", "client", "signedBy", "signedBy2", "createdBy"],
  order: ["consultants", "bidConsultants", "client", "signedBy", "signedBy2", "createdBy"],
  client: ["createdBy"],
  renewal: ["consultants", "client", "createdBy"],
};

export const defaultNotificationRule = () => ({
  name: "",
  cname: "",
  description: "",
  status: "INACTIVE",
  triggerConfig: {
    targetEntity: null,
    event: null,
    watchFields: [],
  },
  recipients: [],
  channels: [],
  template: {
    subject: "",
    body: "",
  },
  debounceSeconds: 0,
});

export const SYSTEM_VARIABLE_LABELS = {
  now: "Current date/time",
};

export const variableLabel = (name) => SYSTEM_VARIABLE_LABELS[name] ?? name;

export const DEFAULT_VARIABLES = [
  "entity.code",
  "entity.status",
  "client.name",
  "product.name",
  "consultant.name",
  "field",
  "oldValue",
  "newValue",
  "changedBy.name",
  "now",
];

export const RECIPIENT_VARIABLES = [
  "recipient.staffId",
  "recipient.name",
  "recipient.cname",
  "recipient.chineseName",
  "recipient.aliasName",
  "recipient.surname",
  "recipient.givenName",
  "recipient.email",
  "recipient.personalEmail",
  "recipient.phone",
  "recipient.phone2",
  "recipient.workTitle",
  "recipient.department",
  "recipient.userType",
  "recipient.status",
];

const withRecipientVariables = (variables) => [
  ...variables,
  ...RECIPIENT_VARIABLES.filter((v) => !variables.includes(v)),
];

export const ENTITY_VARIABLES = {
  debitNote: withRecipientVariables([
    "entity.code",
    "entity.status",
    "entity.paymentStatus",
    "entity.total",
    "entity.currency",
    "entity.policyNumber",
    "client.name",
    "client.email",
    "product.name",
    "provider.name",
    "consultant.name",
    "consultant.staffId",
    "field",
    "oldValue",
    "newValue",
    "changedBy.name",
    "now",
  ]),
  order: withRecipientVariables([
    "entity.code",
    "entity.status",
    "client.name",
    "product.name",
    "provider.name",
    "consultant.name",
    "field",
    "oldValue",
    "newValue",
    "changedBy.name",
    "now",
  ]),
  client: withRecipientVariables([
    "entity.code",
    "entity.name",
    "entity.email",
    "field",
    "oldValue",
    "newValue",
    "changedBy.name",
    "now",
  ]),
  renewal: withRecipientVariables([
    "entity.code",
    "entity.status",
    "client.name",
    "product.name",
    "consultant.name",
    "field",
    "oldValue",
    "newValue",
    "changedBy.name",
    "now",
  ]),
};

export const getVariablesForEntity = (entity) =>
  ENTITY_VARIABLES[entity] ?? withRecipientVariables(DEFAULT_VARIABLES);

export const getWatchFieldsForEntity = (entity) => {
  if (!entity) return [];
  if (entity === "renewal") return RENEWAL_WATCH_FIELDS;
  const mcaEntity = MCA_ENTITIES.find((e) => e.value === entity);
  return mcaEntity?.watchFields ?? [];
};
